import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ManualDataEditor from './ManualDataEditor';
import './ReportPreview.css';

function ReportPreview({ reportId, onClose, onApprove }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  async function fetchReport() {
    try {
      const reportDoc = await getDoc(doc(db, 'monthlyReports', reportId));
      if (reportDoc.exists()) {
        let reportData = { id: reportDoc.id, ...reportDoc.data() };
        
        // Convert projectBlocks (Excel parser format) to projectUpdates (UI format)
        if (reportData.extractedData && reportData.extractedData.projectBlocks) {
          const blocks = reportData.extractedData.projectBlocks;
          
          // Transform projectBlocks to projectUpdates format
          reportData.extractedData.projectUpdates = blocks.map(block => ({
            projectName: `${block.district || ''} - ${block.schools && block.schools[0] ? block.schools[0].substring(0, 50) : 'Project'}`,
            chapter: block.donor || 'Unknown',
            donor: block.donor || 'Unknown',
            beneficiaries: block.totalBeneficiaries || 0,
            fundReceived: block.funds ? block.funds.received : 0,
            fundDisbursed: block.funds ? block.funds.disbursed : 0,
            activities: block.activities || [],
            district: block.district,
            schools: block.schools,
            serialNo: block.serialNo
          }));
          
          // Calculate financial summary from all blocks
          if (!reportData.extractedData.financialSummary) {
            const totalReceived = blocks.reduce((sum, b) => sum + (b.funds ? b.funds.received : 0), 0);
            const totalDisbursed = blocks.reduce((sum, b) => sum + (b.funds ? b.funds.disbursed : 0), 0);
            
            reportData.extractedData.financialSummary = {
              openingBalance: blocks[0] && blocks[0].funds ? blocks[0].funds.opening : 0,
              totalReceived: totalReceived,
              totalDisbursed: totalDisbursed,
              closingBalance: blocks[blocks.length - 1] && blocks[blocks.length - 1].funds ? blocks[blocks.length - 1].funds.closing : 0
            };
          }
          
          // Set newProjects if not already set
          if (!reportData.extractedData.newProjects) {
            reportData.extractedData.newProjects = [];
          }
        }
        
        // If no extracted data yet, use mock data for demo
        if (!reportData.extractedData) {
          reportData.extractedData = {
            financialSummary: {
              openingBalance: 500000,
              totalReceived: 250000,
              totalDisbursed: 180000,
              closingBalance: 570000
            },
            projectUpdates: [
              {
                projectName: "ABC School Education Support",
                chapter: "South Florida",
                donor: "Dr. V.G. Dev",
                beneficiaries: 150,
                fundReceived: 50000,
                fundDisbursed: 45000,
                activities: ["Distributed school supplies", "Conducted teacher training"]
              },
              {
                projectName: "Rural Health Initiative",
                chapter: "Philadelphia",
                donor: "Anonymous",
                beneficiaries: 200,
                fundReceived: 75000,
                fundDisbursed: 60000,
                activities: ["Health camps conducted", "Medicine distribution"]
              }
            ],
            newProjects: [
              {
                title: "Girls Education Program",
                description: "Support for underprivileged girls education",
                district: "Guntur",
                proposedBudget: 100000
              }
            ]
          };
          reportData.metadata = {
            reportTitle: "November 2025 Activity Report",
            month: "2025-11",
            year: 2025
          };
        }
        
        setReport(reportData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching report:', error);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="report-preview-modal">
        <div className="preview-loading">Loading report...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="report-preview-modal">
        <div className="preview-error">Report not found</div>
      </div>
    );
  }

  const { extractedData, metadata } = report;

  return (
    <div className="report-preview-modal">
      <div className="preview-container">
        {/* Header */}
        <div className="preview-header">
          <div>
            <h2>Report Preview</h2>
            <p className="preview-subtitle">{report.fileName}</p>
          </div>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        {/* Metadata Bar */}
        <div className="metadata-bar">
          <div className="metadata-item">
            <span className="metadata-label">Month:</span>
            <span className="metadata-value">{metadata?.reportTitle || 'December 2025'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Uploaded:</span>
            <span className="metadata-value">
              {report.uploadedAt?.toDate().toLocaleDateString()}
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Status:</span>
            <span className={`status-badge status-${report.status}`}>
              {report.status}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="preview-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
          >
            Financial Summary
          </button>
          <button 
            className={`tab-button ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            Project Updates ({extractedData?.projectUpdates?.length || 0})
          </button>
          <button 
            className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            New Projects ({extractedData?.newProjects?.length || 0})
          </button>
        </div>

        {/* Content */}
        <div className="preview-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="stat-cards">
                <div className="stat-card">
                  <div className="stat-icon">💰</div>
                  <div className="stat-info">
                    <div className="stat-label">Total Received</div>
                    <div className="stat-value">
                      ${(extractedData?.financialSummary?.totalReceived || 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📤</div>
                  <div className="stat-info">
                    <div className="stat-label">Total Disbursed</div>
                    <div className="stat-value">
                      ${(extractedData?.financialSummary?.totalDisbursed || 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">📊</div>
                  <div className="stat-info">
                    <div className="stat-label">Projects Updated</div>
                    <div className="stat-value">
                      {extractedData?.projectUpdates?.length || 0}
                    </div>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">✨</div>
                  <div className="stat-info">
                    <div className="stat-label">New Projects</div>
                    <div className="stat-value">
                      {extractedData?.newProjects?.length || 0}
                    </div>
                  </div>
                </div>
              </div>

              <div className="extraction-quality">
                <h3>Extraction Quality</h3>
                <div className="quality-indicators">
                  <div className="quality-item">
                    <span>Financial Data</span>
                    <span className="quality-badge good">✓ Complete</span>
                  </div>
                  <div className="quality-item">
                    <span>Project Updates</span>
                    <span className="quality-badge good">✓ {extractedData?.projectUpdates?.length} found</span>
                  </div>
                  <div className="quality-item">
                    <span>Beneficiary Counts</span>
                    <span className="quality-badge warning">⚠ Needs review</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'financial' && (
            <div className="financial-tab">
              <div className="financial-summary">
                <table className="financial-table">
                  <tbody>
                    <tr>
                      <td className="label">Opening Balance</td>
                      <td className="value">
                        ${(extractedData?.financialSummary?.openingBalance || 0).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="highlight">
                      <td className="label">Funds Received</td>
                      <td className="value positive">
                        +${(extractedData?.financialSummary?.totalReceived || 0).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="highlight">
                      <td className="label">Funds Disbursed</td>
                      <td className="value negative">
                        -${(extractedData?.financialSummary?.totalDisbursed || 0).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="total">
                      <td className="label">Closing Balance</td>
                      <td className="value">
                        ${(extractedData?.financialSummary?.closingBalance || 0).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="projects-tab">
              {extractedData?.projectUpdates?.map((project, index) => (
                <div key={index} className="project-card">
                  <div className="project-header">
                    <h4>{project.projectName}</h4>
                    <span className="project-status">{project.status}</span>
                  </div>
                  <div className="project-details">
                    {project.chapter && (
                      <div className="detail-row">
                        <span className="detail-label">Chapter:</span>
                        <span>{project.chapter}</span>
                      </div>
                    )}
                    {project.donor && (
                      <div className="detail-row">
                        <span className="detail-label">Donor:</span>
                        <span>{project.donor}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Beneficiaries:</span>
                      <span>{project.beneficiaries?.toLocaleString() || 0}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Received:</span>
                      <span className="positive">${project.fundReceived?.toLocaleString() || 0}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Disbursed:</span>
                      <span className="negative">${project.fundDisbursed?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                  {project.activities && project.activities.length > 0 && (
                    <div className="project-activities">
                      <strong>Activities:</strong>
                      <ul>
                        {project.activities.map((activity, i) => (
                          <li key={i}>{activity}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'new' && (
            <div className="new-projects-tab">
              {extractedData?.newProjects?.map((project, index) => (
                <div key={index} className="new-project-card">
                  <h4>{project.title}</h4>
                  <p className="project-description">{project.description}</p>
                  <div className="project-meta">
                    {project.district && (
                      <span className="meta-tag">📍 {project.district}</span>
                    )}
                    {project.proposedBudget > 0 && (
                      <span className="meta-tag">💰 ${project.proposedBudget.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="preview-actions">
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
          <button 
            onClick={() => setShowEditor(true)} 
            className="edit-button"
          >
            ✏️ Edit Manually
          </button>
          <button 
            onClick={() => onApprove(report)} 
            className="approve-button"
            disabled={report.status === 'approved'}
          >
            {report.status === 'approved' ? 'Already Approved' : 'Review & Map →'}
          </button>
        </div>
      </div>

      {/* Manual Editor Modal */}
      {showEditor && (
        <ManualDataEditor
          report={report}
          onSave={() => {
            setShowEditor(false);
            fetchReport(); // Reload data
          }}
          onCancel={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}

export default ReportPreview;
